import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import MuiButton from '@mui/material/Button';
import { Buttons } from '../Buttons';
import EditButton from '../Table/components/EditButton';
import ActivateButton from '../Table/components/ActivateButton';
import locationFeatureFlagService from '../../services/locationFeatureFlagService';
import classes from './FeatureFlagButtonDisabler.module.scss';

const isButtonElement = (element) =>
  element.type === 'button' ||
  element.type === Button ||
  element.type === MuiButton ||
  element.type === Buttons ||
  element.type === EditButton ||
  element.type === ActivateButton ||
  element.props.role === 'button';

const addDisabledStateToButtons = (children) =>
  React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const nestedChildren = addDisabledStateToButtons(child.props.children);
    if (!isButtonElement(child)) {
      return React.cloneElement(child, undefined, nestedChildren);
    }

    const className = [child.props.className, classes.disabled].filter(Boolean).join(' ');
    return React.cloneElement(child, { disabled: true, className }, nestedChildren);
  });

const FeatureFlagButtonDisabler = ({ name, action, children }) => {
  const [isDenied, setIsDenied] = useState(false);

  useEffect(() => {
    let isActive = true;

    if (action !== undefined) {
      setIsDenied(action === 'deny');
      return () => {
        isActive = false;
      };
    }

    setIsDenied(false);
    locationFeatureFlagService
      .lookupLocationFeatureFlag({ featureFlag: name })
      .then(({ data }) => {
        if (isActive) {
          setIsDenied(data?.action === 'deny');
        }
      })
      .catch((error) => {
        console.error(`Could not verify ${name} feature availability.`, error);
        if (isActive) {
          setIsDenied(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [name, action]);

  return <>{isDenied ? addDisabledStateToButtons(children) : children}</>;
};

FeatureFlagButtonDisabler.propTypes = {
  name: PropTypes.string.isRequired,
  action: PropTypes.oneOf(['allow', 'deny', null]),
  children: PropTypes.node.isRequired,
};

export default FeatureFlagButtonDisabler;
